import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaClock,
  FaDollarSign,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaHeartbeat,
  FaShoppingCart,
  FaUsers,
  FaWrench,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { formatAUD } from '../../../utils/formatCurrency';
import '../styles/ResourcesOverview.css';

const DASH_Q = `
  query ResourcesDash($moveLimit: Int) {
    findAllItem {
      id name code quantity available_quantity unit
      reorderLevel perItemCost category description
    }
    findAllInventoryManagement(limit: $moveLimit) {
      id quantityAssigned assignedAt createdAt updateAction remark
      tool { id name code perItemCost }
      project { id name }
    }
    findAllEquipment {
      id assetNumber equipmentType brandModel status nextServiceDate
    }
    findAllPurchaseOrders { id status }
    findAllSuppliers { id status }
    findAllResourceUsage { id usageDate itemId productName qtyUsed }
  }
`;

const LOOKBACK_DAYS = 14;

function onHandOf(item) {
  const avail = Number(item?.available_quantity);
  if (Number.isFinite(avail)) return Math.max(0, avail);
  return Math.max(0, Number(item?.quantity) || 0);
}

function inferCategory(item) {
  const explicit = String(item?.category || '').trim();
  if (explicit) {
    const n = explicit.toLowerCase();
    if (n.includes('chem')) return 'Chemicals';
    if (n.includes('ppe')) return 'PPE';
    if (n.includes('consum')) return 'Consumables';
    if (n.includes('equip')) return 'Equipment';
    if (n.includes('tool')) return 'Tools';
    return explicit;
  }
  const hay = `${item?.name || ''} ${item?.description || ''}`.toLowerCase();
  if (/chem|bleach|disinfect|detergent|acid|solvent|cleaner/.test(hay)) {
    return 'Chemicals';
  }
  if (/glove|ppe|mask|goggle|vest|helmet|respirator/.test(hay)) return 'PPE';
  if (/vacuum|washer|machine|equipment|polisher|scrubber/.test(hay)) {
    return 'Equipment';
  }
  if (/mop|broom|bucket|squeegee|tool/.test(hay)) return 'Tools';
  return 'Consumables';
}

function stockStatus(onHand, reorder) {
  const qty = Number(onHand) || 0;
  const level = Number(reorder);
  const reorderLevel = Number.isFinite(level) && level > 0 ? level : 5;
  if (qty <= 0) return { key: 'out', label: 'Out of Stock' };
  if (qty <= Math.max(1, Math.round(reorderLevel * 0.4))) {
    return { key: 'critical', label: 'Critical' };
  }
  if (qty <= reorderLevel) return { key: 'low', label: 'Low Stock' };
  return { key: 'ok', label: 'In Stock' };
}

function classifyMovement(action) {
  const a = String(action || '').toLowerCase();
  if (a.includes('transfer')) return 'TRF';
  if (a.includes('adjust') || a.includes('lost') || a.includes('shrink')) {
    return 'ADJ';
  }
  if (
    !a ||
    a.includes('add to stock') ||
    a.includes('stock in') ||
    a.includes('restock') ||
    a.includes('return')
  ) {
    return 'IN';
  }
  return 'OUT';
}

function thisMonth(date) {
  if (!date) return false;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
}

function formatShortDate(raw) {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

function daysPast(raw) {
  if (!raw) return 0;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return 0;
  return Math.max(0, Math.round((Date.now() - d.getTime()) / 86400000));
}

function isDueSoon(row) {
  if (String(row.status) === 'ServiceOverdue') return true;
  if (!row.nextServiceDate) return false;
  const d = new Date(row.nextServiceDate);
  if (Number.isNaN(d.getTime())) return false;
  return d <= new Date();
}

function equipmentBucket(row) {
  const t = String(row.equipmentType || row.brandModel || '').toLowerCase();
  if (t.includes('vacuum')) return 'Vacuums';
  if (t.includes('scrubber')) return 'Scrubbers';
  if (t.includes('pressure') || t.includes('washer')) return 'Pressure Washers';
  if (t.includes('extractor')) return 'Extractors';
  return 'Other';
}

function go(navigate, page) {
  navigate({ pathname: '/', search: `?page=${encodeURIComponent(page)}` });
}

export default function ResourcesOverviewDashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await graphqlRequest(DASH_Q, { moveLimit: 400 });
      setItems(Array.isArray(data?.findAllItem) ? data.findAllItem : []);
      setMovements(
        Array.isArray(data?.findAllInventoryManagement)
          ? data.findAllInventoryManagement
          : [],
      );
      setEquipment(
        Array.isArray(data?.findAllEquipment) ? data.findAllEquipment : [],
      );
      setPos(
        Array.isArray(data?.findAllPurchaseOrders)
          ? data.findAllPurchaseOrders
          : [],
      );
      setSuppliers(
        Array.isArray(data?.findAllSuppliers) ? data.findAllSuppliers : [],
      );
      setUsage(
        Array.isArray(data?.findAllResourceUsage)
          ? data.findAllResourceUsage
          : [],
      );
    } catch (err) {
      toast.error(err?.message || 'Failed to load resources dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const enrichedItems = useMemo(
    () =>
      items.map((item) => {
        const onHand = onHandOf(item);
        const reorder = Number(item.reorderLevel);
        const reorderAt = Number.isFinite(reorder) && reorder > 0 ? reorder : 5;
        const unit = String(item.unit || '').trim() || 'unit';
        const cost = Number(item.perItemCost) || 0;
        return {
          ...item,
          onHand,
          reorderAt,
          unit,
          cost,
          value: onHand * cost,
          category: inferCategory(item),
          status: stockStatus(onHand, reorderAt),
        };
      }),
    [items],
  );

  const usageByItem = useMemo(() => {
    const since = Date.now() - LOOKBACK_DAYS * 86400000;
    const map = {};
    usage.forEach((row) => {
      const key = row.itemId || String(row.productName || '').toLowerCase();
      if (!key) return;
      const t = new Date(row.usageDate).getTime();
      if (Number.isNaN(t) || t < since) return;
      map[key] = (map[key] || 0) + (Number(row.qtyUsed) || 0);
    });
    return map;
  }, [usage]);

  const kpis = useMemo(() => {
    const inventoryValue = enrichedItems.reduce((s, i) => s + i.value, 0);
    const low = enrichedItems.filter((i) => i.status.key !== 'ok');
    const critical = low.filter(
      (i) => i.status.key === 'critical' || i.status.key === 'out',
    );
    const due = equipment.filter((e) => {
      if (String(e.status) === 'ServiceOverdue') return true;
      if (!e.nextServiceDate) return false;
      const d = new Date(e.nextServiceDate);
      if (Number.isNaN(d.getTime())) return false;
      const soon = new Date();
      soon.setDate(soon.getDate() + 14);
      return d <= soon;
    });
    const overdueEq = equipment.filter(isDueSoon);
    const inUse = equipment.filter((e) => e.status === 'InUse').length;
    const utilisation =
      equipment.length > 0
        ? Math.round((inUse / equipment.length) * 100)
        : 0;
    const activePos = pos.filter((p) => {
      const s = String(p.status || '').toLowerCase();
      return !s.includes('received') && !s.includes('cancel') && !s.includes('draft');
    });
    const pending = pos.filter((p) =>
      String(p.status || '').toLowerCase().includes('pending'),
    );
    const waste = movements.reduce((sum, rec) => {
      if (!thisMonth(rec.assignedAt || rec.createdAt)) return sum;
      const action = `${rec.updateAction || ''} ${rec.remark || ''}`.toLowerCase();
      if (
        !(
          action.includes('lost') ||
          action.includes('shrink') ||
          action.includes('damage') ||
          action.includes('waste')
        )
      ) {
        return sum;
      }
      const qty = Number(rec.quantityAssigned) || 0;
      const cost = Number(rec.tool?.perItemCost) || 0;
      return sum + qty * cost;
    }, 0);
    const activeSuppliers = suppliers.filter(
      (s) => String(s.status || '').toLowerCase() !== 'inactive',
    );
    return {
      inventoryValue,
      lowCount: low.length,
      criticalCount: critical.length,
      dueCount: due.length,
      overdueCount: overdueEq.length,
      utilisation,
      activePos: activePos.length,
      pending: pending.length,
      waste,
      suppliers: activeSuppliers.length || suppliers.length,
    };
  }, [enrichedItems, equipment, pos, movements, suppliers]);

  const categoryBars = useMemo(() => {
    const order = ['Chemicals', 'Consumables', 'Equipment', 'PPE', 'Tools'];
    const totals = {};
    enrichedItems.forEach((item) => {
      totals[item.category] = (totals[item.category] || 0) + item.value;
    });
    const rows = order.map((name) => ({
      name,
      value: totals[name] || 0,
    }));
    const max = Math.max(1, ...rows.map((r) => r.value));
    return { rows, max };
  }, [enrichedItems]);

  const monthMoves = useMemo(() => {
    const counts = { IN: 0, OUT: 0, ADJ: 0, TRF: 0 };
    movements.forEach((rec) => {
      if (!thisMonth(rec.assignedAt || rec.createdAt)) return;
      const type = classifyMovement(rec.updateAction);
      counts[type] = (counts[type] || 0) + 1;
    });
    const total = counts.IN + counts.OUT + counts.ADJ || 1;
    return { counts, total };
  }, [movements]);

  const lowStockRows = useMemo(
    () =>
      [...enrichedItems]
        .filter((i) => i.status.key !== 'ok')
        .sort((a, b) => a.onHand - b.onHand)
        .slice(0, 5),
    [enrichedItems],
  );

  const recentMoves = useMemo(
    () =>
      [...movements]
        .sort(
          (a, b) =>
            new Date(b.assignedAt || b.createdAt || 0) -
            new Date(a.assignedAt || a.createdAt || 0),
        )
        .slice(0, 6)
        .map((rec) => {
          const type = classifyMovement(rec.updateAction);
          const qty = Number(rec.quantityAssigned) || 0;
          const signed =
            type === 'IN' ? `+${qty}` : type === 'OUT' || type === 'ADJ' ? `-${qty}` : `${qty}`;
          return {
            id: rec.id,
            type,
            name: rec.tool?.name || 'Item',
            signed,
            date: formatShortDate(rec.assignedAt || rec.createdAt),
          };
        }),
    [movements],
  );

  const equipmentGroups = useMemo(() => {
    const names = ['Vacuums', 'Scrubbers', 'Pressure Washers', 'Extractors'];
    return names.map((name) => {
      const rows = equipment.filter((e) => equipmentBucket(e) === name);
      const active = rows.filter(
        (e) => e.status === 'InUse' || e.status === 'Active',
      ).length;
      const overdue = rows.filter(isDueSoon).length;
      const inService = rows.filter((e) => e.status === 'InService').length;
      return { name, active, overdue, inService, total: rows.length };
    });
  }, [equipment]);

  const forecastAlert = useMemo(() => {
    const candidates = enrichedItems
      .map((item) => {
        const used =
          usageByItem[item.id] ||
          usageByItem[String(item.name || '').toLowerCase()] ||
          0;
        const daily = used / LOOKBACK_DAYS;
        const daysLeft = daily > 0 ? item.onHand / daily : null;
        return { ...item, daysLeft };
      })
      .filter((i) => i.daysLeft != null && i.daysLeft > 0 && i.daysLeft <= 14)
      .sort((a, b) => a.daysLeft - b.daysLeft);
    return candidates[0] || null;
  }, [enrichedItems, usageByItem]);

  const overdueEquipment = useMemo(() => {
    return [...equipment]
      .filter(isDueSoon)
      .sort((a, b) => daysPast(b.nextServiceDate) - daysPast(a.nextServiceDate))[0];
  }, [equipment]);

  const donut = useMemo(() => {
    const segs = [
      { key: 'IN', color: '#16a34a', value: monthMoves.counts.IN },
      { key: 'OUT', color: '#dc2626', value: monthMoves.counts.OUT },
      { key: 'ADJ', color: '#f59e0b', value: monthMoves.counts.ADJ },
    ];
    const total = segs.reduce((s, x) => s + x.value, 0) || 1;
    const r = 54;
    const c = 2 * Math.PI * r;
    let offset = 0;
    return segs.map((seg) => {
      const len = (seg.value / total) * c;
      const dash = `${len} ${c - len}`;
      const item = { ...seg, dash, offset, pct: Math.round((seg.value / total) * 100) };
      offset += len;
      return item;
    });
  }, [monthMoves]);

  return (
    <div className="rod-page">
      <h1 className="rod-title">Resources Dashboard</h1>

      {loading ? (
        <div className="rod-empty">Loading resources overview…</div>
      ) : (
        <>
          <section className="rod-kpis">
            <article className="rod-kpi">
              <div className="rod-kpi-ico rod-kpi-ico--purple">
                <FaDollarSign />
              </div>
              <div>
                <div className="rod-kpi-value">
                  {formatAUD(kpis.inventoryValue)}
                </div>
                <div className="rod-kpi-label">Total Inventory Value</div>
              </div>
            </article>
            <article className="rod-kpi">
              <div className="rod-kpi-ico rod-kpi-ico--amber">
                <FaExclamationTriangle />
              </div>
              <div>
                <div className="rod-kpi-value">{kpis.lowCount}</div>
                <div className="rod-kpi-label">Low Stock Items</div>
                <div className="rod-kpi-sub">{kpis.criticalCount} critical</div>
              </div>
            </article>
            <article className="rod-kpi">
              <div className="rod-kpi-ico rod-kpi-ico--amber">
                <FaWrench />
              </div>
              <div>
                <div className="rod-kpi-value">{kpis.dueCount}</div>
                <div className="rod-kpi-label">Equipment Due Service</div>
                <div className="rod-kpi-sub">{kpis.overdueCount} overdue</div>
              </div>
            </article>
            <article className="rod-kpi">
              <div className="rod-kpi-ico rod-kpi-ico--green">
                <FaHeartbeat />
              </div>
              <div>
                <div className="rod-kpi-value">{kpis.utilisation}%</div>
                <div className="rod-kpi-label">Resource Utilisation</div>
              </div>
            </article>
            <article className="rod-kpi">
              <div className="rod-kpi-ico rod-kpi-ico--blue">
                <FaShoppingCart />
              </div>
              <div>
                <div className="rod-kpi-value">{kpis.activePos}</div>
                <div className="rod-kpi-label">Active POs</div>
              </div>
            </article>
            <article className="rod-kpi">
              <div className="rod-kpi-ico rod-kpi-ico--amber">
                <FaClock />
              </div>
              <div>
                <div className="rod-kpi-value">{kpis.pending}</div>
                <div className="rod-kpi-label">Pending Approvals</div>
              </div>
            </article>
            <article className="rod-kpi">
              <div className="rod-kpi-ico rod-kpi-ico--red">
                <FaExclamationCircle />
              </div>
              <div>
                <div className="rod-kpi-value">{formatAUD(kpis.waste)}</div>
                <div className="rod-kpi-label">Resource Waste</div>
              </div>
            </article>
            <article className="rod-kpi">
              <div className="rod-kpi-ico rod-kpi-ico--purple">
                <FaUsers />
              </div>
              <div>
                <div className="rod-kpi-value">{kpis.suppliers}</div>
                <div className="rod-kpi-label">Total Suppliers</div>
              </div>
            </article>
          </section>

          <section className="rod-alerts">
            {kpis.criticalCount > 0 && (
              <div className="rod-alert rod-alert--danger">
                <span>
                  {kpis.criticalCount} item
                  {kpis.criticalCount === 1 ? ' is' : 's are'} critically low
                  (below reorder level) — order now
                </span>
                <button
                  type="button"
                  onClick={() => go(navigate, 'item-description')}
                >
                  View Low Stock
                </button>
              </div>
            )}
            {forecastAlert && (
              <div className="rod-alert rod-alert--warn">
                <span>
                  {forecastAlert.name} stock will run out in{' '}
                  {Math.max(1, Math.round(forecastAlert.daysLeft))} day
                  {Math.round(forecastAlert.daysLeft) === 1 ? '' : 's'} at
                  current usage rate
                </span>
                <button
                  type="button"
                  onClick={() => go(navigate, 'resource-forecasting')}
                >
                  View Forecast
                </button>
              </div>
            )}
            {overdueEquipment && (
              <div className="rod-alert rod-alert--overdue">
                <span>
                  {overdueEquipment.equipmentType || 'Equipment'}{' '}
                  {overdueEquipment.assetNumber
                    ? `#${overdueEquipment.assetNumber}`
                    : ''}{' '}
                  service is overdue
                  {overdueEquipment.nextServiceDate
                    ? ` by ${daysPast(overdueEquipment.nextServiceDate)} day${
                        daysPast(overdueEquipment.nextServiceDate) === 1
                          ? ''
                          : 's'
                      }`
                    : ''}
                </span>
                <button type="button" onClick={() => go(navigate, 'equipment')}>
                  View Equipment
                </button>
              </div>
            )}
            {kpis.criticalCount === 0 &&
              !forecastAlert &&
              !overdueEquipment && (
                <div className="rod-alert rod-alert--ok">
                  No critical stock or service alerts right now.
                </div>
              )}
          </section>

          <section className="rod-charts">
            <div className="rod-card">
              <h2>Inventory Value by Category</h2>
              <div className="rod-bars">
                {categoryBars.rows.map((row) => (
                  <div key={row.name} className="rod-bar-row">
                    <span className="rod-bar-label">{row.name}</span>
                    <div className="rod-bar-track">
                      <div
                        className="rod-bar-fill"
                        style={{
                          width: `${Math.max(
                            row.value > 0 ? 6 : 0,
                            (row.value / categoryBars.max) * 100,
                          )}%`,
                        }}
                      />
                    </div>
                    <span className="rod-bar-val">
                      {formatAUD(row.value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rod-card">
              <h2>Stock Movement This Month</h2>
              <div className="rod-donut-wrap">
                <svg viewBox="0 0 140 140" className="rod-donut">
                  <circle cx="70" cy="70" r="54" fill="none" stroke="#f3f4f6" strokeWidth="16" />
                  {donut.map((seg) => (
                    <circle
                      key={seg.key}
                      cx="70"
                      cy="70"
                      r="54"
                      fill="none"
                      stroke={seg.color}
                      strokeWidth="16"
                      strokeDasharray={seg.dash}
                      strokeDashoffset={-seg.offset}
                      transform="rotate(-90 70 70)"
                    />
                  ))}
                </svg>
                <ul className="rod-legend">
                  <li>
                    <i className="rod-dot rod-dot--in" /> Stock In{' '}
                    <b>{monthMoves.counts.IN}</b>
                  </li>
                  <li>
                    <i className="rod-dot rod-dot--out" /> Stock Out{' '}
                    <b>{monthMoves.counts.OUT}</b>
                  </li>
                  <li>
                    <i className="rod-dot rod-dot--adj" /> Adjustments{' '}
                    <b>{monthMoves.counts.ADJ}</b>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section className="rod-bottom">
            <div className="rod-card">
              <h2>Top 5 Low Stock Items</h2>
              {lowStockRows.length === 0 ? (
                <p className="rod-muted">All products are above reorder level.</p>
              ) : (
                <table className="rod-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Stock</th>
                      <th>Reorder</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lowStockRows.map((row) => (
                      <tr key={row.id}>
                        <td className="rod-item">{row.name}</td>
                        <td>
                          {row.onHand} {row.unit}
                        </td>
                        <td>{row.reorderAt}</td>
                        <td>
                          <span className={`rod-pill rod-pill--${row.status.key}`}>
                            {row.status.label}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <button
                type="button"
                className="rod-order-all"
                onClick={() => go(navigate, 'resource-forecasting')}
              >
                Order All
              </button>
            </div>

            <div className="rod-card">
              <h2>Recent Stock Movements</h2>
              {recentMoves.length === 0 ? (
                <p className="rod-muted">No movements recorded yet.</p>
              ) : (
                <ul className="rod-feed">
                  {recentMoves.map((row) => (
                    <li key={row.id}>
                      <span className={`rod-tag rod-tag--${row.type.toLowerCase()}`}>
                        {row.type}
                      </span>
                      <div>
                        <div className="rod-feed-name">{row.name}</div>
                        <div className="rod-feed-meta">{row.date}</div>
                      </div>
                      <b
                        className={
                          String(row.signed).startsWith('+')
                            ? 'rod-qty-in'
                            : 'rod-qty-out'
                        }
                      >
                        {row.signed}
                      </b>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rod-card">
              <h2>Equipment Status Summary</h2>
              <ul className="rod-eq">
                {equipmentGroups.map((g) => (
                  <li key={g.name}>
                    <span className="rod-eq-name">{g.name}</span>
                    <span className="rod-eq-bits">
                      {g.total === 0 ? (
                        <em>None</em>
                      ) : (
                        <>
                          {g.active > 0 && (
                            <span className="rod-eq-ok">{g.active} active</span>
                          )}
                          {g.overdue > 0 && (
                            <span className="rod-eq-bad">
                              {g.overdue} overdue
                            </span>
                          )}
                          {g.inService > 0 && (
                            <span className="rod-eq-warn">
                              {g.inService} in service
                            </span>
                          )}
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
